import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import QRCode from 'qrcode';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 }; // Riyadh

// Leaflet's default marker icons resolve to broken paths under bundlers.
const LEAFLET_ICON = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

export const googleMapsUrl = (lat, lng) =>
  lat === null || lat === undefined || lng === null || lng === undefined
    ? null
    : `https://www.google.com/maps?q=${lat},${lng}`;

/**
 * Loads the Google Maps JS API once. Rejects if the script fails or the key is
 * rejected, which is what drives the OpenStreetMap fallback below — the key may
 * be restricted to the mobile app, or "Maps JavaScript API" may not be enabled.
 */
let googleLoader = null;
const loadGoogleMaps = () => {
  if (!GOOGLE_KEY) return Promise.reject(new Error('no key'));
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (googleLoader) return googleLoader;

  googleLoader = new Promise((resolve, reject) => {
    // Google reports key/auth problems through this global, not the onload.
    window.gm_authFailure = () => reject(new Error('Google Maps rejected the API key'));

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}&v=weekly`;
    script.async = true;
    script.onload = () =>
      window.google?.maps
        ? resolve(window.google.maps)
        : reject(new Error('Google Maps failed to initialise'));
    script.onerror = () => reject(new Error('Google Maps script failed to load'));
    document.head.appendChild(script);
  });

  return googleLoader;
};

const round = (n) => Number(n.toFixed(6));

/**
 * Click the map to drop a pin. The QR always encodes the Google Maps link for
 * that pin, so scanning it opens the location in Google Maps.
 */
const MapPicker = ({ value, onChange }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const providerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [qr, setQr] = useState(null);
  const [fallback, setFallback] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const lat = value?.latitude ?? null;
  const lng = value?.longitude ?? null;

  // Keep the latest handler without re-creating the map.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const startLeaflet = useCallback(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, { attributionControl: false }).setView(
      [lat ?? DEFAULT_CENTER.lat, lng ?? DEFAULT_CENTER.lng],
      lat != null ? 15 : 11
    );
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
    map.on('click', (e) =>
      onChangeRef.current({ latitude: round(e.latlng.lat), longitude: round(e.latlng.lng) })
    );

    mapRef.current = map;
    providerRef.current = 'leaflet';
    setFallback(true);
    setTimeout(() => map.invalidateSize(), 0);
    // Position is synced by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create the map once: Google when the key works, OpenStreetMap otherwise.
  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !containerRef.current || mapRef.current) return;

        const map = new maps.Map(containerRef.current, {
          center: lat != null ? { lat, lng } : DEFAULT_CENTER,
          zoom: lat != null ? 15 : 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        map.addListener('click', (e) =>
          onChangeRef.current({ latitude: round(e.latLng.lat()), longitude: round(e.latLng.lng()) })
        );

        mapRef.current = map;
        providerRef.current = 'google';
      })
      .catch((err) => {
        console.warn('Falling back to OpenStreetMap:', err.message);
        if (!cancelled) startLeaflet();
      });

    return () => {
      cancelled = true;
      if (providerRef.current === 'leaflet' && mapRef.current) mapRef.current.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the pin in sync with the value, whichever provider is active.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (lat == null || lng == null) {
      if (markerRef.current) {
        if (providerRef.current === 'google') markerRef.current.setMap(null);
        else markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }

    if (providerRef.current === 'google') {
      const position = { lat, lng };
      if (markerRef.current) markerRef.current.setPosition(position);
      else markerRef.current = new window.google.maps.Marker({ position, map });
      map.panTo(position);
    } else {
      if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
      else markerRef.current = L.marker([lat, lng], { icon: LEAFLET_ICON }).addTo(map);
      map.panTo([lat, lng]);
    }
  }, [lat, lng]);

  // The map must re-measure after the container resizes, or it renders into
  // the old box and clicks land in the wrong place.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const id = setTimeout(() => {
      if (providerRef.current === 'google') {
        window.google.maps.event.trigger(map, 'resize');
        if (lat != null && lng != null) map.setCenter({ lat, lng });
      } else {
        map.invalidateSize();
      }
    }, 60);

    return () => clearTimeout(id);
  }, [expanded, lat, lng]);

  // Escape should leave fullscreen, not close the dialog behind it. Capture
  // phase + stopImmediatePropagation preempts the dialog's own handler.
  useEffect(() => {
    if (!expanded) return undefined;

    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      e.preventDefault();
      setExpanded(false);
    };

    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [expanded]);

  // Regenerate the QR whenever the pin moves.
  useEffect(() => {
    const url = googleMapsUrl(lat, lng);
    if (!url) {
      setQr(null);
      return;
    }
    QRCode.toDataURL(url, { width: 180, margin: 1 })
      .then(setQr)
      .catch((err) => console.error('QR generation failed:', err));
  }, [lat, lng]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        onChangeRef.current({
          latitude: round(pos.coords.latitude),
          longitude: round(pos.coords.longitude),
        }),
      (err) => console.error('Geolocation failed:', err)
    );
  };

  const url = googleMapsUrl(lat, lng);

  return (
    <div className={`ad-map-block ${expanded ? 'ad-map-block-full' : ''}`}>
      <div className="ad-map-wrap">
        <div className="ad-map" ref={containerRef} />
        <button
          type="button"
          className="ad-map-expand"
          onClick={() => setExpanded((prev) => !prev)}
          title={t(expanded ? 'property.intake.exitFullscreen' : 'property.intake.fullscreen')}
        >
          <i className={`fas ${expanded ? 'fa-compress' : 'fa-expand'}`}></i>
        </button>
      </div>

      <div className="ad-map-side">
        <p className="ad-hint">{t('property.intake.mapHint')}</p>
        {fallback && <p className="ad-hint">{t('property.intake.mapFallback')}</p>}

        <div className="ad-map-coords">
          <span>
            {t('property.intake.latitude')}: <strong>{lat ?? '-'}</strong>
          </span>
          <span>
            {t('property.intake.longitude')}: <strong>{lng ?? '-'}</strong>
          </span>
        </div>

        <div className="ad-map-actions">
          <button type="button" className="ad-btn" onClick={useMyLocation}>
            <i className="fas fa-location-crosshairs"></i>
            {t('property.intake.myLocation')}
          </button>
          {url && (
            <a className="ad-btn" href={url} target="_blank" rel="noreferrer">
              <i className="fas fa-map-location-dot"></i>
              {t('property.intake.openInMaps')}
            </a>
          )}
        </div>

        {qr && (
          <div className="ad-map-qr">
            <img src={qr} alt={t('property.intake.qr')} />
            <span className="ad-hint">{t('property.intake.qrHint')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapPicker;
